import { useEffect, useRef, useCallback } from 'react';

/**
 * Watches for user inactivity. After `warningAfterMs` of no interaction,
 * calls `onWarning()`. After `logoutAfterMs`, calls `onTimeout()`.
 *
 * Resets the timer on mouse move, key press, touch, or scroll.
 * This is a frontend-only UX guard — the real session boundary is the
 * JWT expiry enforced by the backend.
 *
 * Default: warn at 25 minutes, timeout at 30 minutes (matches JWT_ACCESS_MINUTES=30).
 */
export function useSessionTimeout({
    warningAfterMs = 25 * 60 * 1000,
    logoutAfterMs = 30 * 60 * 1000,
    onWarning,
    onTimeout,
    enabled = true,
}: {
    warningAfterMs?: number;
    logoutAfterMs?: number;
    onWarning: () => void;
    onTimeout: () => void;
    enabled?: boolean;
}) {
    const warningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const logoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const resetTimers = useCallback(() => {
        if (warningTimer.current) clearTimeout(warningTimer.current);
        if (logoutTimer.current) clearTimeout(logoutTimer.current);

        if (!enabled) return;

        warningTimer.current = setTimeout(onWarning, warningAfterMs);
        logoutTimer.current = setTimeout(onTimeout, logoutAfterMs);
    }, [warningAfterMs, logoutAfterMs, onWarning, onTimeout, enabled]);

    useEffect(() => {
        if (!enabled) return;

        const events = ['mousemove', 'keydown', 'touchstart', 'scroll'];
        const handler = () => resetTimers();

        events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
        resetTimers();

        return () => {
            events.forEach((e) => window.removeEventListener(e, handler));
            if (warningTimer.current) clearTimeout(warningTimer.current);
            if (logoutTimer.current) clearTimeout(logoutTimer.current);
        };
    }, [resetTimers, enabled]);
}
